/**
 * Resource Tool Settings Panel
 * Shows resource gallery with category filter and upload action
 */

import React, { useRef, useEffect, useCallback } from 'react';
import { Upload, Trash2, Loader2 } from 'lucide-react';
import { useResourceStore } from '@/stores/resourceStore';
import { useTemplateStore } from '@/stores/designStore';
import { useToolbarStore } from '@/stores/toolbarStore';
import type { Resource, ResourceCategory } from '@/types/resource';
import type { ImageLayer } from '@/types/layers';
import { createAbsolutePosition } from '@/types/position';
import { nanoid } from 'nanoid';

const CATEGORIES: { id: ResourceCategory | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'badges', label: 'Badges' },
  { id: 'devices', label: 'Devices' },
  { id: 'icons', label: 'Icons' },
  { id: 'custom', label: 'My Uploads' },
];

export function ResourceToolSettings() {
  const {
    resources,
    isLoading,
    selectedCategory,
    loadResources,
    addResource,
    deleteResource,
    setSelectedCategory,
  } = useResourceStore();

  const { addLayer, selectLayer } = useTemplateStore();
  const { resetToSelect } = useToolbarStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load resources on mount
  useEffect(() => {
    loadResources();
  }, [loadResources]);

  // Filter resources by category
  const filteredResources =
    selectedCategory === 'all'
      ? resources
      : resources.filter((r) => r.category === selectedCategory);

  // Handle resource click - add to canvas
  const handleResourceClick = useCallback(
    (resource: Resource) => {
      if (resource.type !== 'image') return;

      const imageData = resource.data;
      const layer: ImageLayer = {
        id: nanoid(),
        type: 'image',
        name: resource.name,
        position: createAbsolutePosition(50, 50),
        width: imageData.width,
        height: imageData.height,
        visible: true,
        locked: false,
        opacity: 1,
        content: {
          src: imageData.src,
          objectFit: 'contain',
        },
      };

      addLayer(layer);
      selectLayer(layer.id);
      resetToSelect();
    },
    [addLayer, selectLayer, resetToSelect]
  );

  // Handle file upload
  const handleUpload = useCallback(async () => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      // Read file as data URL
      const reader = new FileReader();
      reader.onload = async (event) => {
        const src = event.target?.result as string;

        // Get image dimensions
        const img = new Image();
        img.onload = async () => {
          await addResource({
            name: file.name.replace(/\.[^.]+$/, ''),
            type: 'image',
            category: 'custom',
            data: {
              src,
              width: img.width,
              height: img.height,
              mimeType: file.type,
            },
            isBuiltin: false,
          });
        };
        img.src = src;
      };
      reader.readAsDataURL(file);

      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [addResource]
  );

  // Handle delete
  const handleDelete = useCallback(
    async (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      if (confirm('Delete this resource?')) {
        await deleteResource(id);
      }
    },
    [deleteResource]
  );

  return (
    <div className="space-y-3">
      {/* Category tabs */}
      <div className="flex flex-wrap gap-1">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`
              px-2 py-1 text-[10px] rounded-md transition-colors
              ${selectedCategory === cat.id
                ? 'bg-primary/20 text-primary'
                : 'bg-white/5 text-fg-muted hover:bg-white/10 hover:text-fg'
              }
            `}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Upload button */}
      <button
        onClick={handleUpload}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm text-fg-muted hover:text-fg transition-colors"
      >
        <Upload size={14} />
        Upload Image
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Resource grid */}
      <div className="max-h-64 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={20} className="animate-spin text-fg-muted" />
          </div>
        ) : filteredResources.length === 0 ? (
          <div className="text-center py-8 text-sm text-fg-subtle">
            No resources found
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {filteredResources.map((resource) => (
              <ResourceThumbnail
                key={resource.id}
                resource={resource}
                onClick={() => handleResourceClick(resource)}
                onDelete={(e) => handleDelete(e, resource.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Help text */}
      <p className="text-[10px] text-fg-subtle">
        Click a resource to add it to your canvas
      </p>
    </div>
  );
}

interface ResourceThumbnailProps {
  resource: Resource;
  onClick: () => void;
  onDelete: (e: React.MouseEvent) => void;
}

function ResourceThumbnail({ resource, onClick, onDelete }: ResourceThumbnailProps) {
  const imageData = resource.type === 'image' ? resource.data : null;

  return (
    <button
      onClick={onClick}
      className="group relative aspect-square bg-white/5 rounded-lg overflow-hidden hover:bg-white/10 transition-colors border border-white/5 hover:border-white/20"
    >
      {imageData && (
        <img
          src={imageData.src}
          alt={resource.name}
          className="w-full h-full object-contain p-2"
        />
      )}

      {/* Name overlay */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-1">
        <p className="text-[9px] text-white truncate">{resource.name}</p>
      </div>

      {/* Delete button (only for custom resources) */}
      {!resource.isBuiltin && (
        <button
          onClick={onDelete}
          className="absolute top-1 right-1 p-1 bg-black/50 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500/50"
        >
          <Trash2 size={10} className="text-white" />
        </button>
      )}
    </button>
  );
}
